import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { AdminShell, type AdminSectionId } from "@/components/admin/AdminShell";
import {
  BannersSectionAdmin,
  OverviewSection,
  PlansSectionAdmin,
} from "@/components/admin/CatalogSections";
import {
  BrandSection,
  CoverageSection,
  FooterSection,
  HighlightsSection,
  TestimonialsSection,
  HostingSection,
  PartnersSection,
  PhoneSection,
  ServicesSection,
  InternetSection,
  CamerasSection,
  AppsSectionAdmin,
  BlogSection,
  JobsSection,
  StoresSection,
  AboutSection,
  PromoSection,
  IntegrationsSection,
} from "@/components/admin/ContentSections";
import { ApplicationsSection } from "@/components/admin/ApplicationsSection";
import { DownloadsSection } from "@/components/admin/DownloadsSectionAdmin";
import { UsersSection } from "@/components/admin/UsersSection";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Painel administrativo — Contato Internet" },
      {
        name: "description",
        content: "Gestão de planos, banners, textos e imagens do site Contato.",
      },
      { property: "og:title", content: "Painel administrativo — Contato Internet" },
      { property: "og:description", content: "Gestão completa do conteúdo do site Contato." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

const ALL_SECTION_IDS: AdminSectionId[] = [
  "overview",
  "plans",
  "banners",
  "highlights",
  "testimonials",
  "services",
  "coverage",
  "partners",
  "phone",
  "hosting",
  "internet",
  "cameras",
  "apps",
  "blog",
  "jobs",
  "applications",
  "downloads",
  "stores",
  "about",
  "promo",
  "integrations",
  "brand",
  "footer",
  "users",
];

function AdminPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"checking" | "admin" | "editor" | "denied" | "error">(
    "checking",
  );
  const [allowed, setAllowed] = useState<AdminSectionId[]>([]);
  const [section, setSection] = useState<AdminSectionId>("overview");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) {
        if (!cancelled) setStatus("denied");
        return;
      }

      const { data: roleRow } = await supabase
        .from("user_roles")
        .select("id")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      if (roleRow) {
        if (!cancelled) {
          setAllowed(ALL_SECTION_IDS);
          setStatus("admin");
        }
        return;
      }

      // Bootstrap do primeiro admin: checagem feita no banco (RPC
      // SECURITY DEFINER), nunca contando linhas pelo client — a RLS de
      // user_roles só deixa cada usuário ver a própria linha, então um
      // count feito pelo client sempre dá 0 pra quem não é admin.
      const { data: claimed, error: claimError } = await supabase.rpc("claim_first_admin");
      if (claimError) {
        if (!cancelled) setStatus("error");
        return;
      }
      if (claimed) {
        if (!cancelled) {
          setAllowed(ALL_SECTION_IDS);
          setStatus("admin");
        }
        return;
      }

      // Não é admin — vê só as seções liberadas pra ele (usuário "editor").
      const { data: accessRows } = await supabase
        .from("user_section_access")
        .select("section")
        .eq("user_id", uid);
      const granted = (accessRows ?? [])
        .map((r) => r.section as AdminSectionId)
        .filter((s) => ALL_SECTION_IDS.includes(s));
      if (granted.length > 0) {
        if (!cancelled) {
          setAllowed(["overview", ...granted]);
          setSection("overview");
          setStatus("editor");
        }
        return;
      }

      if (!cancelled) setStatus("denied");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (status === "checking") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "denied" || status === "error") {
    return (
      <div className="mx-auto max-w-md px-5 py-24 text-center">
        <h1 className="text-2xl font-extrabold">
          {status === "error" ? "Não foi possível verificar o acesso" : "Acesso restrito"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {status === "error"
            ? "Tente recarregar a página. Se o problema continuar, entre novamente."
            : "Sua conta não tem permissão de administrador."}
        </p>
        <Button className="mt-6 rounded-full" onClick={signOut}>
          Sair
        </Button>
      </div>
    );
  }

  const canSee = (id: AdminSectionId) => allowed.includes(id);

  return (
    <AdminShell
      active={section}
      onSelect={setSection}
      allowed={status === "admin" ? undefined : allowed}
    >
      {section === "overview" && canSee("overview") && <OverviewSection />}
      {section === "plans" && canSee("plans") && <PlansSectionAdmin />}
      {section === "banners" && canSee("banners") && <BannersSectionAdmin />}
      {section === "highlights" && canSee("highlights") && <HighlightsSection />}
      {section === "testimonials" && canSee("testimonials") && <TestimonialsSection />}
      {section === "services" && canSee("services") && <ServicesSection />}
      {section === "coverage" && canSee("coverage") && <CoverageSection />}
      {section === "partners" && canSee("partners") && <PartnersSection />}
      {section === "phone" && canSee("phone") && <PhoneSection />}
      {section === "hosting" && canSee("hosting") && <HostingSection />}
      {section === "internet" && canSee("internet") && <InternetSection />}
      {section === "cameras" && canSee("cameras") && <CamerasSection />}
      {section === "apps" && canSee("apps") && <AppsSectionAdmin />}
      {section === "blog" && canSee("blog") && <BlogSection />}
      {section === "jobs" && canSee("jobs") && <JobsSection />}
      {section === "applications" && canSee("applications") && <ApplicationsSection />}
      {section === "downloads" && canSee("downloads") && <DownloadsSection />}
      {section === "stores" && canSee("stores") && <StoresSection />}
      {section === "about" && canSee("about") && <AboutSection />}
      {section === "promo" && canSee("promo") && <PromoSection />}
      {section === "integrations" && canSee("integrations") && <IntegrationsSection />}
      {section === "brand" && canSee("brand") && <BrandSection />}
      {section === "footer" && canSee("footer") && <FooterSection />}
      {section === "users" && status === "admin" && <UsersSection />}
    </AdminShell>
  );
}
